import { createContext, useContext } from 'react';
import Cookies from 'js-cookie';
import { User, AuthTokens } from '@/types';

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (_email: string, _password: string) => Promise<void>;
  register: (_email: string, _password: string, _name?: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const setAuthTokens = (tokens: AuthTokens) => {
  const cookieOptions = {
    expires: 1, // 1 day for access token
    sameSite: 'lax' as const,
    secure: process.env.NODE_ENV === 'production',
  };
  
  const refreshCookieOptions = {
    expires: 7, // 7 days for refresh token
    sameSite: 'lax' as const,
    secure: process.env.NODE_ENV === 'production',
  };
  
  Cookies.set('accessToken', tokens.accessToken, cookieOptions);
  Cookies.set('refreshToken', tokens.refreshToken, refreshCookieOptions);
};

export const clearAuthTokens = () => {
  Cookies.remove('accessToken');
  Cookies.remove('refreshToken');
};

export const getAccessToken = () => {
  return Cookies.get('accessToken');
};

export const getRefreshToken = () => {
  return Cookies.get('refreshToken');
};

export const isAuthenticated = () => {
  return !!getAccessToken();
};